import { useArticleContentRefMap } from "@EditArticleContainer/shared/contexts/ArticleContentRefContext";
import { getParagraphEditedData } from "@EditArticleContainer/shared/hooks/useParagraph";
import {
  REF_CODE_DEAL_LICENSEE_NAME_MA,
  REF_CODE_DEAL_LICENSEE_RESPONSIBILITY_MA,
  REF_CODE_DEAL_LICENSOR_NAME_MA,
  REF_CODE_DEAL_LICENSOR_RESPONSIBILITY_MA,
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_MILESTONE,
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_VALUE,
  REF_CODE_FINANCIAL_TERMS_MA_NUM_SHARES,
  REF_CODE_FINANCIAL_TERMS_MA_OWNERSHIP_EXCHANGE,
  REF_CODE_FINANCIAL_TERMS_MA_TERM_DETAILS,
  REF_CODE_FINANCIAL_TERMS_MA_TOTAL_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_TOTAL_VALUE,
  REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_VALUE,
  REF_CODE_KEY_COMMENTS_LIST_QUOTE,
  REF_CODE_KEY_COMMENTS_LIST_SPOKESPERSON,
  REF_CODE_KEY_RIGHTS_MA_DRUG,
  REF_CODE_KEY_RIGHTS_MA_INDICATION,
  REF_CODE_MA_STRUCTURE_MECHANICAL,
  REF_CODE_MA_STRUCTURE_NOTES,
  REF_CODE_MA_STRUCTURE_TAGS,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { FinancialTermsMAContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/financialTermsMAContent.types";
import { KeyCommentsListContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/keyCommentsListContent.types";
import { KeyRightsMAContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/keyRightsMAContent.types";
import { MAStructureContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/maStructureContent.types";
import { PartnerAxisMAContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/partnerAxisMAContent.types";
import {
  cloneContent,
  isEmptyContent,
  overwriteFromRef,
} from "@EditArticleContainer/shared/utils/mergeParagraphContentFromRef";

const SECTION_CODE = "TY002";

export const useCreateDealMAContent = () => {
  const refMap = useArticleContentRefMap();

  const buildPartnerAxisMA = async (): Promise<
    PartnerAxisMAContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.partnerAxisMA,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as PartnerAxisMAContent);

    overwriteFromRef(
      refMap,
      next.text["Licensor(MA)"]?.[0]?.["Licensor Name(MA)"],
      REF_CODE_DEAL_LICENSOR_NAME_MA,
    );
    overwriteFromRef(
      refMap,
      next.text["Licensor Responsibility & Rights"],
      REF_CODE_DEAL_LICENSOR_RESPONSIBILITY_MA,
    );
    overwriteFromRef(
      refMap,
      next.text["Licensee(MA)"]?.[0]?.["Licensee Name(MA)"],
      REF_CODE_DEAL_LICENSEE_NAME_MA,
    );
    overwriteFromRef(
      refMap,
      next.text["Licensee Responsibility & Rights (MA)"],
      REF_CODE_DEAL_LICENSEE_RESPONSIBILITY_MA,
    );

    return next;
  };

  const buildKeyRightsMA = async (): Promise<
    KeyRightsMAContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.keyRightsMA,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as KeyRightsMAContent);

    next.text["Rights Scope"]?.forEach((item, index) => {
      overwriteFromRef(
        refMap,
        item.Drug,
        createRefCode(REF_CODE_KEY_RIGHTS_MA_DRUG, index),
      );
      overwriteFromRef(
        refMap,
        item.Indication,
        createRefCode(REF_CODE_KEY_RIGHTS_MA_INDICATION, index),
      );
    });

    return next;
  };

  const buildFinancialTermsMA = async (): Promise<
    FinancialTermsMAContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.financialTermsMA,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as FinancialTermsMAContent);
    const terms = next.text["Merge & Acquisition terms"];

    overwriteFromRef(
      refMap,
      terms["Upfront Payment (MA)"],
      REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_VALUE,
    );
    overwriteFromRef(
      refMap,
      terms["Upfront Price Per Share (MA)"],
      REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_PRICE_PER_SHARE,
    );
    overwriteFromRef(
      refMap,
      terms["Total Payment (MA)"],
      REF_CODE_FINANCIAL_TERMS_MA_TOTAL_VALUE,
    );
    overwriteFromRef(
      refMap,
      terms["Price Per Share"],
      REF_CODE_FINANCIAL_TERMS_MA_TOTAL_PRICE_PER_SHARE,
    );
    overwriteFromRef(
      refMap,
      terms["Number of acquiring shares"],
      REF_CODE_FINANCIAL_TERMS_MA_NUM_SHARES,
    );
    overwriteFromRef(
      refMap,
      terms["Merger Financial Terms (MA)"],
      REF_CODE_FINANCIAL_TERMS_MA_OWNERSHIP_EXCHANGE,
    );
    overwriteFromRef(
      refMap,
      terms["Other Financial Terms (MA)"],
      REF_CODE_FINANCIAL_TERMS_MA_TERM_DETAILS,
    );

    terms["Contingent Payment"]?.forEach((contingent, index) => {
      overwriteFromRef(
        refMap,
        contingent["Contingent Payment (MA)"],
        createRefCode(REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_VALUE, index),
      );
      overwriteFromRef(
        refMap,
        contingent["Contingent Price per share (MA)"],
        createRefCode(
          REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_PRICE_PER_SHARE,
          index,
        ),
      );
      overwriteFromRef(
        refMap,
        contingent["Contingent Milestone (MA)"],
        createRefCode(REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_MILESTONE, index),
      );
    });

    return next;
  };

  const buildMAStructure = async (): Promise<
    MAStructureContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.maStructure,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as MAStructureContent);
    const structure = next.text["Deal Structure"];

    overwriteFromRef(
      refMap,
      structure["Mechanical Structure"],
      REF_CODE_MA_STRUCTURE_MECHANICAL,
    );
    overwriteFromRef(
      refMap,
      structure["Structural Tags"],
      REF_CODE_MA_STRUCTURE_TAGS,
    );
    overwriteFromRef(
      refMap,
      structure["Structural Notes"],
      REF_CODE_MA_STRUCTURE_NOTES,
    );

    return next;
  };

  const buildKeyCommentsList = async (): Promise<
    KeyCommentsListContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.keyCommentsList,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as KeyCommentsListContent);

    next.text["Key Comment List"]?.forEach((item, index) => {
      overwriteFromRef(
        refMap,
        item["Comment Quote"],
        createRefCode(REF_CODE_KEY_COMMENTS_LIST_QUOTE, index),
      );
      overwriteFromRef(
        refMap,
        item["Comment Spokesperson"],
        createRefCode(REF_CODE_KEY_COMMENTS_LIST_SPOKESPERSON, index),
      );
    });

    return next;
  };

  return {
    buildPartnerAxisMA,
    buildKeyRightsMA,
    buildFinancialTermsMA,
    buildMAStructure,
    buildKeyCommentsList,
  };
};
